import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, GripVertical, Eye, EyeOff, Pencil, X, LayoutList, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useCMS } from "@/hooks/useCMS";
import { MediaPicker } from "@/components/MediaPicker";
import { RichEditor } from "@/components/RichEditor";
import { SECTION_TEMPLATES } from "@/components/TemplateSections";

const CORE_FIELDS = [
  { name: "eyebrow", label: "Eyebrow (EN)" },
  { name: "heading", label: "Heading (EN)" },
  { name: "subheading", label: "Sub-heading (EN)" },
  { name: "bullets", label: "Bullet list (EN) — one per line", area: true },
  { name: "eyebrow_fr", label: "Eyebrow (FR)" },
  { name: "heading_fr", label: "Heading (FR)" },
  { name: "subheading_fr", label: "Sub-heading (FR)" },
  { name: "bullets_fr", label: "Bullet list (FR) — one per line", area: true },
];

const PALETTE = ["#2CACD4", "#DB1A5C", "#CAE295", "#0F172A", "#F7F5EF", "#1C8C8C"];
const POSITIONS = ["center", "top", "bottom", "left", "right"];

export default function SectionsManager() {
  const { reload } = useCMS();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState("home");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/content/sections").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const pages = useMemo(() => [...new Set(items.map((i) => i.page))].sort(), [items]);
  const rows = useMemo(() => items.filter((i) => i.page === page).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [items, page]);

  const onDrop = async (toIndex) => {
    if (dragIndex === null || dragIndex === toIndex) { setDragIndex(null); return; }
    const next = [...rows];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(toIndex, 0, moved);
    setDragIndex(null);
    setItems((all) => all.map((it) => { const idx = next.findIndex((n) => n.id === it.id); return idx >= 0 ? { ...it, order: idx } : it; }));
    try {
      await api.put(`/content/sections/reorder`, { ids: next.map((i) => i.id) });
      toast.success("Order saved"); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const toggle = async (sec) => {
    const enabled = !(sec.enabled !== false);
    setItems((all) => all.map((it) => (it.id === sec.id ? { ...it, enabled } : it)));
    try { await api.put(`/content/sections/${sec.id}`, { enabled }); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/content/sections/${editing.id}`, editing);
      toast.success("Section saved"); setEditing(null); load(); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addTemplate = async (tpl) => {
    try {
      const body = {
        page, template: tpl.template, label: tpl.name, enabled: true,
        key: `tpl_${tpl.template}_${Date.now().toString(36)}`,
        order: rows.length, ...tpl.seed,
      };
      const { data } = await api.post(`/content/sections`, body);
      toast.success(`${tpl.name} added`);
      setAdding(false);
      load(); reload();
      setEditing(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (sec) => {
    if (!window.confirm("Delete this section block?")) return;
    try {
      await api.delete(`/content/sections/${sec.id}`);
      toast.success("Section deleted"); load(); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2CACD4]" /></div>;

  const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4]";
  const tpl = editing?.template ? SECTION_TEMPLATES.find((t) => t.template === editing.template) : null;
  const extra = [];
  if (editing?.template && ["hero", "cta"].includes(editing.template)) {
    extra.push({ name: "cta_label", label: "Button label (EN)" });
    extra.push({ name: "cta_link", label: "Button link" });
    extra.push({ name: "cta_label_fr", label: "Button label (FR)" });
  }

  const setF = (k, v) => setEditing((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="admin-sections">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-black uppercase text-3xl flex items-center gap-3"><LayoutList className="text-[#2CACD4]" /> Page Sections</h1>
          <p className="mt-1 text-muted-foreground text-sm">Drag to reorder, toggle on/off, set backgrounds, and add ready-made blocks. Changes appear live.</p>
        </div>
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white hover:bg-[#2CACD4] hover:text-slate-950 font-bold px-5 py-2.5 transition-colors cursor-pointer" data-testid="section-add-btn">
          <Plus size={18} /> Add section
        </button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="sections-page-tabs">
        {pages.map((p) => (
          <button key={p} onClick={() => setPage(p)} className={`rounded-full px-4 py-1.5 text-sm font-bold capitalize transition-colors cursor-pointer ${page === p ? "bg-[#0F172A] text-white" : "bg-card border border-border hover:border-[#2CACD4]"}`} data-testid={`sections-tab-${p}`}>{p}</button>
        ))}
      </div>

      <div className="mt-5 space-y-2">
        {rows.map((sec, idx) => {
          const on = sec.enabled !== false;
          return (
            <div
              key={sec.id}
              draggable
              onDragStart={() => setDragIndex(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              className={`flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 ${dragIndex === idx ? "opacity-40" : ""} ${!on ? "opacity-60" : ""}`}
              data-testid="section-row"
            >
              <GripVertical size={18} className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground" data-testid="section-drag" />
              {(sec.bg_color || sec.bg_image) && (
                <span className="h-6 w-6 shrink-0 rounded-md border border-border bg-cover bg-center" style={sec.bg_image ? { backgroundImage: `url(${sec.bg_image})` } : { backgroundColor: sec.bg_color }} title="Has background" />
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate flex items-center gap-2">
                  {sec.label || sec.key}
                  {sec.template && <span className="rounded-full bg-[#2CACD4]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#1C8C8C]">Block</span>}
                </p>
                <p className="text-xs text-muted-foreground truncate">{sec.heading || sec.eyebrow || sec.key}</p>
              </div>
              <button onClick={() => toggle(sec)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors cursor-pointer ${on ? "bg-[#CAE295]/50 text-green-800 hover:bg-[#CAE295]" : "bg-muted text-muted-foreground"}`} data-testid="section-toggle">
                {on ? <><Eye size={14} /> On</> : <><EyeOff size={14} /> Off</>}
              </button>
              <button onClick={() => setEditing(sec)} className="rounded-lg p-2 hover:bg-muted cursor-pointer" aria-label="Edit" data-testid="section-edit"><Pencil size={15} /></button>
              {sec.template && <button onClick={() => remove(sec)} className="rounded-lg p-2 text-[#DB1A5C] hover:bg-[#DB1A5C]/10 cursor-pointer" aria-label="Delete" data-testid="section-delete"><Trash2 size={15} /></button>}
            </div>
          );
        })}
        {rows.length === 0 && <p className="text-muted-foreground text-sm py-8 text-center">No sections for this page.</p>}
      </div>

      {/* Template picker */}
      {adding && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10" onClick={() => setAdding(false)}>
          <div className="w-full max-w-2xl rounded-3xl bg-card shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="section-template-modal">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h2 className="font-display font-black uppercase text-xl">Add a section to <span className="capitalize text-[#2CACD4]">{page}</span></h2>
              <button onClick={() => setAdding(false)} className="rounded-lg p-1.5 hover:bg-muted cursor-pointer"><X size={20} /></button>
            </div>
            <div className="grid max-h-[68vh] grid-cols-1 gap-3 overflow-y-auto px-6 py-5 sm:grid-cols-2">
              {SECTION_TEMPLATES.map((t) => (
                <button key={t.template} onClick={() => addTemplate(t)} className="group rounded-2xl border border-border bg-background p-4 text-left transition-all hover:border-[#2CACD4] hover:shadow-md cursor-pointer" data-testid={`tpl-pick-${t.template}`}>
                  <p className="font-display font-black uppercase">{t.name}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#2CACD4] opacity-0 transition-opacity group-hover:opacity-100"><Plus size={13} /> Add</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10" onClick={() => setEditing(null)}>
          <div className={`w-full ${editing.template === "richtext" ? "max-w-3xl" : "max-w-lg"} rounded-3xl bg-card shadow-2xl`} onClick={(e) => e.stopPropagation()} data-testid="section-modal">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h2 className="font-display font-black uppercase text-xl">{editing.label || editing.key}</h2>
              <button onClick={() => setEditing(null)} className="rounded-lg p-1.5 hover:bg-muted cursor-pointer"><X size={20} /></button>
            </div>
            <div className="max-h-[68vh] overflow-y-auto px-6 py-5 space-y-4">
              {tpl && <p className="rounded-xl bg-[#2CACD4]/10 px-3 py-2 text-xs text-[#1C8C8C]">{tpl.hint}</p>}

              {CORE_FIELDS.map((f) => (
                <div key={f.name}>
                  <label className="block text-sm font-semibold mb-1.5">{f.label}</label>
                  {f.area ? (
                    <textarea rows={3} value={editing[f.name] || ""} onChange={(e) => setF(f.name, e.target.value)} className={inputCls} data-testid={`section-field-${f.name}`} />
                  ) : (
                    <input value={editing[f.name] || ""} onChange={(e) => setF(f.name, e.target.value)} className={inputCls} data-testid={`section-field-${f.name}`} />
                  )}
                </div>
              ))}

              {extra.map((f) => (
                <div key={f.name}>
                  <label className="block text-sm font-semibold mb-1.5">{f.label}</label>
                  <input value={editing[f.name] || ""} onChange={(e) => setF(f.name, e.target.value)} className={inputCls} data-testid={`section-field-${f.name}`} />
                </div>
              ))}

              {editing.template === "richtext" && (
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Body (EN)</label>
                  <RichEditor value={editing.body || ""} onChange={(v) => setF("body", v)} />
                  <label className="mt-4 block text-sm font-semibold mb-1.5">Body (FR)</label>
                  <RichEditor value={editing.body_fr || ""} onChange={(v) => setF("body_fr", v)} />
                </div>
              )}

              {editing.template === "cta" && (
                <div>
                  <label className="block text-sm font-semibold mb-1.5">Banner colour</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={editing.accent || "#DB1A5C"} onChange={(e) => setF("accent", e.target.value)} className="h-10 w-12 rounded-lg border border-border cursor-pointer" data-testid="section-accent" />
                    <input value={editing.accent || ""} onChange={(e) => setF("accent", e.target.value)} placeholder="#DB1A5C" className={inputCls} />
                  </div>
                </div>
              )}

              {/* Background controls */}
              <div className="rounded-2xl border border-border bg-background p-4">
                <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Section background</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {PALETTE.map((c) => (
                    <button key={c} type="button" onClick={() => setF("bg_color", c)} className={`h-8 w-8 rounded-full border-2 ${editing.bg_color === c ? "border-foreground" : "border-border"} cursor-pointer`} style={{ backgroundColor: c }} data-testid={`section-bg-swatch-${c}`} />
                  ))}
                  <input type="color" value={editing.bg_color || "#ffffff"} onChange={(e) => setF("bg_color", e.target.value)} className="h-8 w-10 rounded-lg border border-border cursor-pointer" aria-label="Custom colour" data-testid="section-bg-color" />
                  <button type="button" onClick={() => { setF("bg_color", ""); }} className="rounded-full bg-muted px-3 py-1.5 text-xs font-bold hover:bg-muted/70 cursor-pointer" data-testid="section-bg-clear-color">Clear colour</button>
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-semibold mb-1.5">Background image (optional — overrides colour)</label>
                  <input value={editing.bg_image || ""} onChange={(e) => setF("bg_image", e.target.value)} placeholder="https://…" className={inputCls} data-testid="section-bg-image" />
                  <MediaPicker onPick={(url) => setF("bg_image", url)} />
                  {editing.bg_image && (
                    <>
                      <div className="mt-3 h-28 w-full overflow-hidden rounded-xl border border-border bg-cover" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,${(Number(editing.bg_overlay) || 0) / 100}),rgba(0,0,0,${(Number(editing.bg_overlay) || 0) / 100})),url(${editing.bg_image})`, backgroundSize: "cover", backgroundPosition: editing.bg_image_pos || "center" }} data-testid="section-bg-preview" />
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <select value={editing.bg_image_pos || "center"} onChange={(e) => setF("bg_image_pos", e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs cursor-pointer" data-testid="section-bg-pos">
                          {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <div className="flex flex-1 items-center gap-2">
                          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Darken {Number(editing.bg_overlay) || 0}%</span>
                          <input type="range" min="0" max="80" step="5" value={Number(editing.bg_overlay) || 0} onChange={(e) => setF("bg_overlay", Number(e.target.value))} className="flex-1 accent-[#2CACD4] cursor-pointer" data-testid="section-bg-overlay" />
                        </div>
                        <button type="button" onClick={() => { setF("bg_image", ""); setF("bg_overlay", 0); }} className="rounded-full bg-muted px-3 py-1.5 text-xs font-bold hover:bg-muted/70 cursor-pointer" data-testid="section-bg-clear-image">Remove image</button>
                      </div>
                    </>
                  )}
                </div>

                <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={editing.text_light === "yes"} onChange={(e) => setF("text_light", e.target.checked ? "yes" : "no")} className="h-4 w-4 accent-[#2CACD4] cursor-pointer" data-testid="section-text-light" />
                  Use light text (for dark backgrounds)
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
              <button onClick={() => setEditing(null)} className="rounded-full px-5 py-2.5 font-bold hover:bg-muted cursor-pointer">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white hover:bg-[#2CACD4] hover:text-slate-950 font-bold px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-60" data-testid="section-save">
                {saving ? <Loader2 className="animate-spin" size={16} /> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
